import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Bot, CandlestickChart, ArrowRightLeft, Newspaper, Radio, Settings, LogOut, Shield, Wallet, FileCheck, UserCheck, ClipboardList, Search } from 'lucide-react';

// Icono personalizado para Atenea — cabeza humanoide IA con nodo neuronal
const AteneaIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    {/* Cabeza humanoide */}
    <path d="M12 3C9 3 7 5.5 7 8c0 2 1 3.5 2.5 4.5V14h5v-1.5C16 11.5 17 10 17 8c0-2.5-2-5-5-5z" />
    {/* Cuello */}
    <path d="M9.5 14v1.5h5V14" />
    {/* Hombros / base */}
    <path d="M7 15.5C5 16 4 17 4 18.5h16c0-1.5-1-2.5-3-3" />
    {/* Ojos — puntos brillantes */}
    <circle cx="10" cy="8" r="0.8" fill={color} stroke="none" />
    <circle cx="14" cy="8" r="0.8" fill={color} stroke="none" />
    {/* Nodo central neuronal en la frente */}
    <circle cx="12" cy="5.5" r="0.6" fill={color} stroke="none" />
    {/* Líneas neurales laterales */}
    <path d="M7 7.5 L5.5 6" strokeWidth="1" opacity="0.6" />
    <path d="M17 7.5 L18.5 6" strokeWidth="1" opacity="0.6" />
    <path d="M7 9 L5.5 9.5" strokeWidth="1" opacity="0.6" />
    <path d="M17 9 L18.5 9.5" strokeWidth="1" opacity="0.6" />
  </svg>
);
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { lang, setLang, t } = useLanguage();
  const [hovered, setHovered] = useState(null); // { label, top }

  const showTooltip = (e, label) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHovered({ label, top: rect.top + rect.height / 2 });
  };
  const hideTooltip = () => setHovered(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: t('dashboard'), path: '/dashboard' },
    { icon: Bot, label: t('bots'), path: '/bots' },
    { icon: CandlestickChart, label: t('trading'), path: '/trading' },
    { icon: ArrowRightLeft, label: t('arbitrage'), path: '/arbitrage' },
    { icon: Newspaper, label: t('news'), path: '/news' },
    { icon: AteneaIcon, label: 'Atenea Estadísticas', path: '/ia' },
    { icon: AteneaIcon, label: 'Atenea IA', path: '/atenea-trader' },
    { icon: Search, label: 'Scanner', path: '/scanner' },
    { icon: Radio, label: t('live'), path: '/livedata' },
    { icon: UserCheck, label: 'Copy Trading', path: '/copy-trading' },
    { icon: ClipboardList, label: 'Historial', path: '/history' },
    { icon: Wallet, label: 'Mi Cuenta', path: '/cuenta' },
    { icon: Settings, label: t('settings'), path: '/settings' },
  ];

  // Add admin link if user is admin
  const isAdmin = user?.role === 'admin';

  return (
    <div className="w-20 bg-[#121212] border-r border-[#27272A] flex flex-col items-center py-3 space-y-2" style={{ height: '100vh', flexShrink: 0 }}>
      {/* Logo */}
      <div className="text-[#00D4FF] font-bold text-xl mb-2 text-center">
        AI
      </div>

      {/* Menu Items */}
      <nav className="flex-1 flex flex-col space-y-1 overflow-y-auto overflow-x-hidden">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          // Atenea Trader usa el mismo ícono que Atenea Estadísticas (a
          // propósito, es la misma Atenea) -- pero se veían IDÉNTICOS en
          // el menú, sin forma de distinguir cuál es cuál de un vistazo.
          // Se le da un color propio (verde, el mismo tema que ya usa su
          // página) en vez del celeste genérico del resto del menú.
          const esAteneaTrader = item.path === '/atenea-trader';

          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`sidebar-${item.label.toLowerCase()}-link`}
              onMouseEnter={(e) => showTooltip(e, item.label)}
              onMouseLeave={hideTooltip}
              className={`w-10 h-10 flex items-center justify-center rounded-sm transition-all ${
                isActive
                  ? esAteneaTrader
                    ? 'bg-[#00FF88] text-black shadow-[0_0_15px_rgba(0,255,136,0.3)]'
                    : 'bg-[#00D4FF] text-black shadow-[0_0_15px_rgba(0,212,255,0.3)]'
                  : esAteneaTrader
                    ? 'text-[#6fdba8] hover:text-[#00FF88] hover:bg-[#1E1E1E]'
                    : 'text-[#A1A1AA] hover:text-[#00D4FF] hover:bg-[#1E1E1E]'
              }`}
            >
              <Icon size={20} />
            </Link>
          );
        })}

        {/* Admin Panel Link */}
        {isAdmin && (
          <Link
            to="/admin"
            data-testid="sidebar-admin-link"
            onMouseEnter={(e) => showTooltip(e, 'Admin Panel')}
            onMouseLeave={hideTooltip}
            className={`w-10 h-10 flex items-center justify-center rounded-sm transition-all ${
              location.pathname === '/admin'
                ? 'bg-red-500 text-white shadow-[0_0_15px_rgba(239,68,68,0.3)]'
                : 'text-red-400 hover:text-red-500 hover:bg-red-500/10'
            }`}
          >
            <Shield size={20} />
          </Link>
        )}
      </nav>

      {/* Tooltip flotante — fuera del flujo del nav, no afecta su scroll */}
      {hovered && (
        <div
          key={hovered.label}
          className="fixed z-50 bg-[#1E1E1E] border border-[#27272A] text-white text-xs font-medium px-2.5 py-1.5 rounded-md whitespace-nowrap shadow-lg shadow-black/40 pointer-events-none"
          style={{
            top: hovered.top,
            left: 84,
            transform: 'translateY(-50%)',
            animation: 'aegisTooltipPop 150ms ease-out',
          }}
        >
          {hovered.label}
          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-[#27272A]" />
        </div>
      )}
      <style>{`
        @keyframes aegisTooltipPop {
          0% { opacity: 0; transform: translateY(-50%) scale(0.75); }
          100% { opacity: 1; transform: translateY(-50%) scale(1); }
        }
      `}</style>

      {/* Idioma ES/EN */}
      <button
        onClick={() => setLang(lang === 'es' ? 'en' : 'es')}
        className="text-[#A1A1AA] hover:text-white text-xs font-bold border border-[#27272A] rounded px-2 py-1"
        title={t('language')}
      >
        {lang.toUpperCase()}
      </button>

      {/* Logout */}
      <button
        onClick={handleLogout}
        data-testid="sidebar-logout-button"
        className="w-10 h-10 flex items-center justify-center rounded-sm text-[#FF3B30] hover:bg-red-500/10 transition-all"
        title="Logout"
      >
        <LogOut size={20} />
      </button>
    </div>
  );
};

export default Sidebar;